/**
 * ModulesMenu — GLOBAL snippet (iter 241).
 *
 * "Other Modules" dropdown inside `<PlatformHeader />`. Renders every
 * enabled module from ``GET /api/platform/modules`` with its brand
 * colour dot + description. Admins turn modules on/off in the
 * HustleSuite Admin (``/admin/modules``); disabled modules disappear
 * from every module's nav bar instantly (no deploy needed).
 *
 * Snippet-first: DO NOT create per-module variants. This is THE list.
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Grid3x3 } from "lucide-react";
import Tip from "@/components/Tip";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");

let CACHE = null;   // module-scoped cache so opening the dropdown on
                    // any page after the first fetch is instant.

export default function ModulesMenu({ testid = "modules-menu", label = "Other Modules", className = "" }) {
  const [open, setOpen] = useState(false);
  const [modules, setModules] = useState(CACHE || []);
  const [loading, setLoading] = useState(!CACHE);
  const [pos, setPos] = useState({ top: 0, right: 0 });
  const ref = useRef(null);
  const btnRef = useRef(null);

  // Reposition the portalled panel below the trigger, right-aligned.
  // NOTE: the panel uses `position: fixed`, so top/right MUST be
  // viewport-relative — do NOT add window.scrollY/scrollX. Otherwise
  // the panel appears one full scroll-distance below the trigger and
  // ends up off-screen after the user has scrolled down (iter313h bug
  // reported on hustleeats.au).
  const reposition = () => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({
      top:   r.bottom + 8,
      right: Math.max(8, window.innerWidth - r.right),
    });
  };
  useLayoutEffect(() => {
    if (!open) return;
    reposition();
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [open]);

  // iter308 (04-Feb-2026): The outside-click listener MUST be attached
  //   only while `open` is true AND armed one microtask later. Otherwise
  //   the same mousedown that opens the panel (button click) can — in
  //   certain browser + React StrictMode combos — also close it inside
  //   the same paint tick, producing the "opens for a split second then
  //   snaps shut" bug the user has reported repeatedly. The `armed`
  //   guard is the bulletproof fix.
  useEffect(() => {
    if (!open) return;
    let armed = false;
    const armId = setTimeout(() => { armed = true; }, 0);
    function onDown(e) {
      if (!armed) return;
      if (ref.current && ref.current.contains(e.target)) return;
      // The portal panel is not a DOM descendant of ref.current, so
      // check its data-testid tree explicitly.
      if (e.target && e.target.closest?.(`[data-testid="${testid}-panel"]`)) return;
      setOpen(false);
    }
    function onKey(e)  { if (e.key === "Escape") setOpen(false); }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(armId);
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, testid]);

  useEffect(() => {
    if (CACHE) return;
    setLoading(true);
    fetch(`${API}/api/platform/modules`)
      .then((r) => (r.ok ? r.json() : { modules: [] }))
      .then((d) => {
        CACHE = d.modules || [];
        setModules(CACHE);
      })
      .catch(() => setModules([]))
      .finally(() => setLoading(false));

    // Admin page dispatches this event after a toggle so we refetch.
    const onChange = () => {
      CACHE = null;
      fetch(`${API}/api/platform/modules`)
        .then((r) => r.json())
        .then((d) => { CACHE = d.modules || []; setModules(CACHE); })
        .catch(() => {});
    };
    window.addEventListener("hs:modules-changed", onChange);
    return () => window.removeEventListener("hs:modules-changed", onChange);
  }, []);

  if (!loading && modules.length === 0) return null;

  return (
    <div ref={ref} className="relative">
      <Tip label={`${label} — every module on the Suite`}>
      <button
        ref={btnRef}
        type="button"
        data-testid={`${testid}-trigger`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={label}
        className={"inline-flex items-center gap-1 text-sm tracking-wide transition-colors " +
                   "text-[#121614] dark:text-stone-100 hover:text-[#C05746] " +
                   className}
      >
        <Grid3x3 className="w-3.5 h-3.5" strokeWidth={2} aria-hidden="true" />
        <span>{label}</span>
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={2} aria-hidden="true" />
      </button>
      </Tip>
      {open && typeof document !== "undefined" && createPortal(
        <div
          data-testid={`${testid}-panel`}
          style={{ top: pos.top, right: pos.right }}
          className="fixed w-[22rem] max-w-[calc(100vw-16px)] max-h-[70vh] overflow-y-auto z-[9999]
                     rounded-xl border border-stone-200 dark:border-stone-700
                     bg-white dark:bg-stone-900 shadow-2xl"
        >
          <div className="px-4 py-3 border-b border-stone-200 dark:border-stone-700">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-stone-700 dark:text-stone-200">
              The HustleSuite
            </p>
            <p className="text-[11px] text-stone-600 dark:text-stone-300 mt-0.5">
              One app, one login — every module on the Suite.
            </p>
          </div>
          <div className="p-2 grid grid-cols-1 sm:grid-cols-2 gap-1">
            {/* Pinned "HustleSuite Home" affordance — always the first
                tile so users can jump back to the flagship home page
                from anywhere in the Suite. Not driven by the API list
                (that's the list of MODULES, not the home itself). */}
            <a
              key="__suite_home"
              href="https://hustlesuite.au/"
              data-testid={`${testid}-item-suite`}
              title="HustleSuite Home — the flagship landing"
              onClick={() => setOpen(false)}
              rel="noopener"
              className="flex items-start gap-2.5 px-3 py-2 rounded-lg
                         hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors
                         sm:col-span-2 border-b border-stone-100 dark:border-stone-800 mb-1 pb-3"
            >
              <span
                className="mt-1 shrink-0 w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: "#121614" }}
                aria-hidden="true"
              />
              <span className="flex flex-col leading-tight">
                <span className="text-[13px] font-semibold text-stone-900 dark:text-stone-100">
                  HustleSuite Home
                </span>
                <span className="text-[10.5px] text-stone-600 dark:text-stone-300 line-clamp-2">
                  The flagship home — one app, one login, every module on the Suite.
                </span>
              </span>
            </a>
            {modules.map((m) => {
              // iter254: Every "Other Modules" tile links to the module's
              //   apex root (e.g. https://hustleeats.au), never to a
              //   deep inner path like /eat. The user rule is: hamburger
              //   sub-menu items live on subroutes; the top-level module
              //   grid always lands on the bare-domain sales page.
              const rawHref = m.host ? `${m.host}/` : (m.home || "/");
              const external = Boolean(m.host);
              // iter253: When jumping to another module host, stamp a
              //   ?from=<current-url> query so ReturnToChip on the
              //   destination site can offer a one-tap way back. Only
              //   applied to external hops — same-origin nav needs no
              //   help from a return chip.
              let href = rawHref;
              if (external && typeof window !== "undefined") {
                try {
                  const u = new URL(rawHref);
                  u.searchParams.set("from", window.location.href);
                  href = u.toString();
                } catch {
                  href = rawHref;
                }
              }
              return (
                <a
                  key={m.key}
                  href={href}
                  data-testid={`${testid}-item-${m.key}`}
                  title={`${m.label} — ${m.desc || ""}`.trim()}
                  onClick={() => setOpen(false)}
                  {...(external ? { rel: "noopener" } : {})}
                  className="flex items-start gap-2.5 px-3 py-2 rounded-lg
                             hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors"
                >
                  <span
                    className="mt-1 shrink-0 w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: m.color || "#7c3aed" }}
                    aria-hidden="true"
                  />
                  <span className="flex flex-col leading-tight">
                    <span className="text-[13px] font-semibold text-stone-900 dark:text-stone-100">
                      {m.label}
                    </span>
                    <span className="text-[10.5px] text-stone-600 dark:text-stone-300 line-clamp-2">
                      {m.desc}
                    </span>
                  </span>
                </a>
              );
            })}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
